/*
 * What Hyperswarm expects of a connection, taught to a relayed one.
 *
 * In a browser every connection is a stream from @hyperswarm/dht-relay rather
 * than a NoiseSecretStream, and it is not quite the same shape. Two of the
 * differences are load-bearing, and both surface in exactly one place: when
 * the same peer connects a second time while an older connection to it is
 * still on the books — which is what every reconnect after a dropped link
 * looks like.
 *
 *   rawBytesRead / rawBytesWritten
 *     Hyperswarm keeps the new connection if the old one has carried traffic
 *     both ways — the peer would not be reconnecting over a link that still
 *     worked. A relayed stream counts nothing, so the old connection always
 *     looked brand new, and half the time (it is decided by comparing keys)
 *     the new, working connection was the one thrown away. The dead one stayed,
 *     and everything asked of that peer went into it and never came back.
 *
 *   sendKeepAlive()
 *     Called on the connection Hyperswarm decided to keep in that same branch.
 *     A relayed stream has no such method, so the call threw inside the relay's
 *     message handler — after the new connection had been accepted by the relay
 *     and before Hyperswarm could either keep or close it.
 *
 * Nothing here changes what goes over the wire. An empty write is what a
 * keep-alive is anywhere else, and the peer's secret stream discards it.
 */

const adapted = new WeakSet()

/** Patch the class of this stream, once, and hand the stream back. */
export function adaptRelayStream (stream) {
  if (!stream || typeof stream !== 'object') return stream
  const proto = Object.getPrototypeOf(stream)
  if (!proto || adapted.has(proto)) return stream
  adapted.add(proto)

  // A real secret stream (the relay's non-custodial mode) already has all of
  // this; only the relay's own stream class needs it.
  if (typeof proto.sendKeepAlive === 'function') return stream

  const push = proto.push
  proto.push = function (data) {
    if (data) this.rawBytesRead = (this.rawBytesRead || 0) + (data.byteLength || 0)
    return push.call(this, data)
  }

  const writev = proto._writev
  if (typeof writev === 'function') {
    proto._writev = function (batch, cb) {
      let bytes = 0
      for (const chunk of batch) bytes += (chunk && chunk.byteLength) || 0
      this.rawBytesWritten = (this.rawBytesWritten || 0) + bytes
      return writev.call(this, batch, cb)
    }
  }

  proto.sendKeepAlive = function sendKeepAlive () {
    if (this.destroying || this.destroyed) return
    try {
      this.write(typeof this.alloc === 'function' ? this.alloc(0) : new Uint8Array(0))
    } catch { /* closing under us; nothing to keep alive */ }
  }

  return stream
}

/**
 * Wrap a relay DHT node so every connection it hands out is adapted before
 * Hyperswarm sees it — client connections from connect(), and server ones
 * through the listener Hyperswarm passes to createServer().
 */
export function adaptRelayNode (dht) {
  const connect = dht.connect.bind(dht)
  dht.connect = (...args) => adaptRelayStream(connect(...args))

  const createServer = dht.createServer.bind(dht)
  dht.createServer = (opts, listener) => {
    if (typeof opts === 'function') {
      listener = opts
      opts = {}
    }
    const wrapped = listener ? (conn) => listener(adaptRelayStream(conn)) : undefined
    return createServer(opts || {}, wrapped)
  }
  return dht
}
